/* The Google Map itself: basemap, markers, clustering, label collision, and the
   card anchored to the selected venue.
 *
 * Everything imperative about the map lives here so the workspace above can stay
 * declarative — it hands down a venue list and a selection, and receives events.
 * The Maps API is not reactive and does not diff, so this component is the
 * translation layer between Lit's model and a mutable third-party canvas.
 *
 * Three things are easy to get wrong and are commented at the point they happen:
 * the theme rebuild, the two-tier marker DOM, and what runs on `idle`.
 *
 * On shadow DOM: the map is constructed inside this component's shadow root, and
 * so is every marker's content (the API re-parents it under the map div). That
 * means the pin styles below apply to markers without a single global rule —
 * v1 needs `.iw-pin` in app.css. It works because `disableDefaultUI` leaves the
 * API with no chrome of its own to style; if a default control is ever enabled,
 * check it before trusting it, since the API's own stylesheet goes into
 * document.head and does not cross the boundary.
 */

import { LitElement, html, css, render, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { shadowReset } from '../../design/base.js';
import { loadMaps } from '../../core/maps-loader.js';
import {
  estimateWidth,
  resolveLabels,
  shiftLat as shiftLatBy,
  type Placeable,
} from '../../../../packages/core/index.js';
import type { Venue } from '../../core/types.js';
import { kindMeta } from './kinds.js';
import './venue-card.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';

/** Everything this component tracks per rendered venue. */
interface MarkerRecord {
  venue: Venue;
  marker: google.maps.marker.AdvancedMarkerElement;
  dot: HTMLElement;
  /** Built on first use: only ~40 of 500 venues are ever pills at once. */
  pill?: HTMLElement;
  mode: 'dot' | 'pill';
}

/** Where the world starts, before any venue is known. Roughly India. */
const HOME = { lat: 15, lng: 78 };
const HOME_ZOOM = 4;

/**
 * Clustering applies only below this zoom. From zoom 9 up, every venue is its
 * own marker and the collision pass alone decides what gets a label — clusters
 * at street level would hide exactly the detail the user zoomed in for.
 */
const CLUSTER_MAX_ZOOM = 8;

/**
 * Below this many markers, don't cluster at all.
 *
 * Clustering exists to stop five hundred pins turning into mud at world zoom. A
 * filtered set is not mud — it is the answer to a question the user just asked,
 * and rolling it into a single "48" bubble hides exactly what they searched for.
 * Found by searching "ubud": the results fit at zoom 7.3, just under the zoom
 * threshold, and the whole of Bali collapsed into one circle.
 */
const CLUSTER_MIN_MARKERS = 100;

/** A lone pin would otherwise fit-bounds to street level, which reads as lost. */
const FIT_MAX_ZOOM = 11;

/**
 * How close selecting a venue from the list gets you.
 *
 * 12, not 14: at 14 a venue fills the screen with its own street and the twenty
 * other saved places around it drop off the map, which is the opposite of what
 * the list is for. 12 lands on the neighbourhood with its neighbours still
 * visible. Never zooms *out* — if you are already closer than this, selecting a
 * row moves the camera and leaves the zoom alone.
 */
const SELECT_ZOOM = 12;

/** Flight duration bounds, ms. Long enough to follow, short enough to not wait. */
const FLIGHT_MIN_MS = 320;
const FLIGHT_MAX_MS = 900;

@customElement('map-canvas')
export class MapCanvas extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: block;
        position: relative;
        height: 100%;
        background: var(--surface-2);
      }
      #map {
        width: 100%;
        height: 100%;
      }
      .overlay {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        padding: var(--space-6);
        text-align: center;
      }
      /* The map bleeds under the status bar on mobile (ADR 0012 decision 10),
         which puts the system's own glyphs over an arbitrary basemap. A short
         wash of --bg keeps them legible without a bar of chrome — invisible
         until the basemap underneath happens to be light, and 0px tall wherever
         there is no inset at all. */
      .scrim {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: calc(var(--safe-t) + var(--space-2));
        background: linear-gradient(
          to bottom,
          color-mix(in oklab, var(--bg) 70%, transparent),
          transparent
        );
        pointer-events: none;
      }

      /* ── marker DOM ──────────────────────────────────────────
         Both variants are children of the map div, inside this shadow root, so
         these rules reach them. --kc is set inline per marker from kinds.ts. */

      /* Filled with its kind, not outlined in it. An outlined dot puts only a 2px
         ring of colour on the map — at 22px across a basemap that is itself
         coloured, the kind barely registers and every venue reads the same. The
         fill is the marker's whole job: 491 of the ~505 markers on screen are
         dots, so this is what the map actually looks like.

         The ring is now --surface, which separates the marker from whatever it
         sits on (park green, water, a road) far better than a shadow alone.

         --accent-ink for the glyph: it is defined as the ink that sits on a
         filled accent and flips per theme, which is exactly the problem here —
         the light hues are deep and want a white glyph, the dark ones are bright
         and want a dark one. */
      .dot {
        display: grid;
        place-items: center;
        width: 22px;
        height: 22px;
        border: 2px solid var(--surface);
        border-radius: var(--radius-full);
        background: var(--kc);
        color: var(--accent-ink);
        --icon-size: 12px;
        box-shadow: 0 1px 4px rgb(0 0 0 / 0.35);
        cursor: pointer;
      }
      .dot:active {
        filter: brightness(0.85);
      }
      /* Lifted clear of its own anchor point so the tail below can occupy that
         6px and put its tip on the coordinate. Without the lift the pill's
         bottom edge sits on the venue and the label only implies which point it
         belongs to — which is ambiguous the moment two venues are close. */
      .pill {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        max-width: 180px;
        padding: 3px 10px 3px 3px;
        border: 1px solid var(--kc);
        border-radius: var(--radius-full);
        background: var(--surface);
        color: var(--text);
        font-family: var(--font-ui);
        font-size: 0.78rem;
        line-height: 1.35;
        white-space: nowrap;
        box-shadow: 0 1px 5px rgb(0 0 0 / 0.28);
        cursor: pointer;
        transform: translateY(-6px);
      }
      /* On .pill, which carries the tail as its own ::after — so the filter
         takes both and the two cannot seam apart mid-press. Dimming rather than
         a wash because the pill has three backgrounds (default, visited tint,
         selected fill) and this is correct for all of them without restating
         any. */
      .pill:active {
        filter: brightness(0.92);
      }
      /* The tail. A rotated square rather than a border triangle, so its two
         visible edges carry the pill's own 1px border and it can be filled with
         background:inherit — which keeps it correct for all three pill backgrounds
         (default, visited tint, selected fill) with no extra rules.

         Geometry: an 8px square offset 4px below the edge has its centre exactly
         on the pill's bottom edge, so half its diagonal (5.66px) protrudes —
         against the 6px lift, the tip lands on the coordinate. Its opaque fill
         also covers the pill's bottom border across the join, so the two read as
         one shape rather than a box with a diamond stuck to it. */
      .pill::after {
        content: '';
        position: absolute;
        bottom: -4px;
        left: 50%;
        width: 8px;
        height: 8px;
        border-right: 1px solid var(--kc);
        border-bottom: 1px solid var(--kc);
        background: inherit;
        transform: translateX(-50%) rotate(45deg);
      }
      /* The pill's icon sits in a filled disc — the same fill the bare dot has,
         at the same size. A pill is then literally a dot that has been given room
         for its name, and the two presentations of one venue stop looking like
         two different things. It also puts a solid block of the kind's colour on
         a pill whose body has to stay pale for the label to be readable. */
      .pill .disc {
        display: grid;
        place-items: center;
        flex: none;
        width: 20px;
        height: 20px;
        border-radius: var(--radius-full);
        background: var(--kc);
        color: var(--accent-ink);
        --icon-size: 12px;
      }
      .pill .label {
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .pill .n {
        flex: none;
        padding: 0 5px;
        border-radius: var(--radius-full);
        background: color-mix(in oklab, var(--kc) var(--k-tint), transparent);
        color: var(--kc);
        font-family: var(--font-mono);
        font-size: 0.68rem;
      }
      /* The selected venue: the whole pill takes the fill the disc had, and the
         disc inverts to become the hole in it. Reading the two states as one
         gesture — the colour spreading out from the disc to the whole pill — is
         what makes the selection obvious without a second visual language.

         --accent-ink rather than #fff: in the dark theme the hues are bright and
         white-on-marigold measures 2.0:1. --accent-ink is dark there and white on
         light, which is the entire reason the token exists. */
      .pill.active {
        border-color: var(--kc);
        background: var(--kc);
        color: var(--accent-ink);
        box-shadow: 0 2px 10px rgb(0 0 0 / 0.4);
      }
      .pill.active .disc {
        background: var(--accent-ink);
        color: var(--kc);
      }
      .pill.active .n {
        background: color-mix(in oklab, var(--accent-ink) 22%, transparent);
        color: var(--accent-ink);
      }
      /* Visited: the tick replaces the kind icon inside the disc, which is the
         signal that survives at dot size. The ring lightens as confirmation —
         it is the only per-venue state the user writes from this screen. */
      .dot.visited {
        border-color: color-mix(in oklab, var(--kc) 45%, var(--surface));
      }
      .cluster {
        display: grid;
        place-items: center;
        width: 32px;
        height: 32px;
        border: 1px solid var(--accent);
        border-radius: var(--radius-full);
        background: var(--bg);
        color: var(--accent);
        font-family: var(--font-mono);
        font-size: 0.74rem;
        box-shadow: 0 1px 6px rgb(0 0 0 / 0.3);
      }
    `,
  ];

  /** Venues to draw. Already filtered by the workspace; not-geocoded ones are
      passed through and skipped here, so both halves see the same list. */
  @property({ attribute: false }) venues: Venue[] = [];
  /** map_place_id of the selected venue, or null. */
  @property({ attribute: false }) selected: number | null = null;
  /**
   * What to frame on first paint instead of the whole set — the venues in the
   * user's own country (ADR 0005).
   *
   * A property consumed at the fit decision below, rather than the workspace
   * calling `fitTo` itself, and deliberately: a direct call races this
   * component's own first-paint fit depending on whether the Maps API resolved
   * first. That is the same race `pendingFocus` exists for, so this uses the
   * same slot instead of opening a second one.
   *
   * The split of responsibility: the workspace decides *which* venues are home,
   * this component decides *when* a fit happens. Nothing here knows what a
   * country is. Empty is the normal "couldn't tell, or none there" answer and
   * falls through to framing everything.
   */
  @property({ attribute: false }) home: readonly Venue[] = [];
  @property() apiKey = '';
  @property() mapId = '';
  /** Follows the app theme; a change tears the basemap down (see `sync`). */
  @property() scheme: 'light' | 'dark' = 'dark';
  /**
   * Pixels of this element obscured at the bottom by the mobile sheet.
   *
   * Only the camera reads it, and only to put a venue in the middle of what is
   * actually visible rather than in the middle of the div (ADR 0012 decision 6).
   * 0 is the desktop case exactly — the docked panel is a sibling, not an
   * overlay, which is the whole point of ADR 0005 decision 2.
   *
   * The status-bar wash at the top is measured here rather than passed in,
   * because this component renders it; `topInset` covers what it does not.
   */
  @property({ type: Number }) bottomInset = 0;

  /**
   * Pixels obscured at the top by the workspace's floating controls.
   *
   * Desktop only, and read only when framing a set of venues — without it the
   * chip row sits on top of whatever pins land against the top edge. The larger
   * of this and the status-bar wash wins rather than their sum: the two never
   * coexist, since the wash is 0 wherever the controls are rendered.
   */
  @property({ type: Number }) topInset = 0;
  /**
   * Whether a selected venue gets the floating card.
   *
   * False on mobile, where `place-detail` in the sheet is the venue's detail view
   * and a 240px card over a 360px map would be a second surface answering the
   * same question (ADR 0012 decision 8).
   */
  @property({ type: Boolean }) cards = true;

  @state() private failed = '';
  @state() private ready = false;

  @query('#map') private host?: HTMLElement;
  @query('.scrim') private scrim?: HTMLElement;

  private map?: google.maps.Map;
  private clusterer?: MarkerClusterer;
  private records: MarkerRecord[] = [];
  private cardMarker?: google.maps.marker.AdvancedMarkerElement;
  private listeners: google.maps.MapsEventListener[] = [];
  /** Which scheme the live map was constructed with. */
  private builtScheme = '';
  /** First paint fits to the data; later ones must not move the camera. */
  private fitDone = false;
  /**
   * A venue asked for before the map existed.
   *
   * Only reachable since Detail's place chip: every other caller acts on a map
   * the user is already looking at, but a chip can mount this workspace and ask
   * it to focus a venue in the same tick, while the Maps API is still loading.
   * Dropping the request silently left the user on the world view wondering
   * what the click did.
   */
  private pendingFocus?: Venue;
  /** In-flight camera animation, so a second selection cancels the first. */
  private flight = 0;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardown();
  }

  override updated(changed: Map<string, unknown>): void {
    void this.sync(changed);
  }

  // ── lifecycle ──────────────────────────────────────────────

  /**
   * Reconcile the live map against the current properties.
   *
   * Ordered deliberately: a scheme change rebuilds the map, which discards every
   * marker, so the marker refresh has to come after it and unconditionally.
   */
  private async sync(changed: Map<string, unknown>): Promise<void> {
    if (!this.apiKey || this.failed) return;

    const rebuilding = this.map != null && this.builtScheme !== this.scheme;
    if (!this.map || rebuilding) {
      await this.build();
      if (!this.map) return;
      this.drawMarkers();
      this.drawCard();
      return;
    }
    if (changed.has('venues')) this.drawMarkers();
    if (changed.has('venues') || changed.has('selected')) {
      this.restyleSelection();
      this.drawCard();
    }
  }

  private async build(): Promise<void> {
    // Preserve the camera across a rebuild — a theme flip must not also teleport
    // the user back to the world view.
    const camera = this.map
      ? { center: this.map.getCenter()?.toJSON(), zoom: this.map.getZoom() }
      : null;
    this.teardown();

    try {
      await loadMaps(this.apiKey);
    } catch (e) {
      this.failed = e instanceof Error ? e.message : String(e);
      return;
    }
    // `host` can vanish while the API loads (workspace switched, then this
    // resolves) — Lit keeps the element alive but the query can still miss.
    const el = this.host;
    if (!el) return;
    el.replaceChildren();

    this.map = new google.maps.Map(el, {
      mapId: this.mapId || undefined,
      // Init-time only. There is no setter and no supported way to change it on
      // a live Map, which is the entire reason `sync` rebuilds instead of
      // updating — see the theme note in the class comment.
      colorScheme: this.scheme === 'dark' ? 'DARK' : 'LIGHT',
      // No zoom buttons, no fullscreen, no Street View pegman: the app supplies
      // its own controls, and it also means the API injects no chrome whose
      // stylesheet would have to cross the shadow boundary.
      disableDefaultUI: true,
      // Explicit, not 'auto'. Auto resolves to cooperative — two fingers to pan —
      // when the page is scrollable, and while ours is not, "probably greedy" is
      // not a thing to leave in the only way a thumb can move this map.
      gestureHandling: 'greedy',
      center: camera?.center ?? HOME,
      zoom: camera?.zoom ?? HOME_ZOOM,
    });
    this.builtScheme = this.scheme;
    this.ready = true;

    this.listeners.push(
      // Everything that must settle before it can be computed. `idle` fires once
      // per interaction end, not per frame, so a drag runs this once.
      this.map.addListener('idle', () => {
        this.runCollision();
        this.emitCamera();
      }),
      // Tapping bare map dismisses the card, matching every map app.
      this.map.addListener('click', () => this.emit('venue-deselect', null)),
    );
  }

  /**
   * Remove every marker from the map, whichever way it got there.
   *
   * Both halves are needed: the clusterer owns the markers it was given and only
   * detaches them via setMap(null), while an unclustered set was attached one by
   * one and the clusterer knows nothing about it. Miss either and the old
   * markers stay on the map invisibly, accumulating on every filter change.
   */
  private detachMarkers(): void {
    this.clusterer?.setMap(null);
    this.clusterer = undefined;
    for (const rec of this.records) rec.marker.map = null;
    this.records = [];
  }

  private teardown(): void {
    cancelAnimationFrame(this.flight);
    for (const l of this.listeners) l.remove();
    this.listeners = [];
    this.detachMarkers();
    if (this.cardMarker) this.cardMarker.map = null;
    this.cardMarker = undefined;
    this.map = undefined;
    this.ready = false;
  }

  // ── markers ────────────────────────────────────────────────

  private drawMarkers(): void {
    if (!this.map) return;
    this.detachMarkers();

    const pinned = this.venues.filter((v) => v.geocoded && v.lat != null && v.lng != null);
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const venue of pinned) {
      const dot = this.buildDot(venue);
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: venue.lat as number, lng: venue.lng as number },
        content: dot,
        gmpClickable: true,
        // Selected on top, then busier venues — so where pills do overlap, the
        // one with more behind it wins the front.
        zIndex: venue.map_place_id === this.selected ? 1_000_000 : venue.reels.length,
      });
      marker.addListener('click', () => this.emit('venue-select', venue.map_place_id));
      const record: MarkerRecord = { venue, marker, dot, mode: 'dot' };
      this.records.push(record);
      markers.push(marker);
    }

    if (markers.length >= CLUSTER_MIN_MARKERS) {
      this.clusterer = new MarkerClusterer({
        map: this.map,
        markers,
        algorithm: new SuperClusterAlgorithm({ maxZoom: CLUSTER_MAX_ZOOM, radius: 80 }),
        renderer: {
          render: ({ count, position }) => {
            const el = document.createElement('div');
            el.className = 'cluster';
            el.textContent = String(count);
            return new google.maps.marker.AdvancedMarkerElement({ position, content: el });
          },
        },
      });
    } else {
      // Small set: straight onto the map, and the collision pass alone decides
      // what gets a label at any zoom.
      for (const m of markers) m.map = this.map;
    }

    // A focus asked for while the map was loading wins over the first-paint fit
    // — the user named a venue, which is a stronger instruction than "show me
    // everything". Consuming `fitDone` too, so the fit doesn't fire afterwards.
    if (this.pendingFocus) {
      const venue = this.pendingFocus;
      this.pendingFocus = undefined;
      this.fitDone = true;
      this.restyleSelection();
      this.panTo(venue);
    } else if (!this.fitDone && (this.homePinned.length || pinned.length)) {
      // Open on the user's own country when they have venues in it, else on the
      // whole library. Tested against `homePinned` rather than `home` so an
      // un-geocoded home venue can't consume the fit and leave the map parked
      // on the default — `fitTo` would filter it out and return without moving.
      this.fitDone = true;
      this.fitTo(this.homePinned.length ? this.homePinned : pinned);
    } else {
      this.restyleSelection();
      this.runCollision();
    }
  }

  /** The compact variant: a kind-coloured ring. Always built. */
  private buildDot(v: Venue): HTMLElement {
    const meta = kindMeta(v.kind, v.geocoded);
    const el = document.createElement('div');
    el.className = `dot${v.visited ? ' visited' : ''}`;
    el.style.setProperty('--kc', meta.color);
    render(html`<g-icon name=${v.visited ? 'check' : meta.icon}></g-icon>`, el);
    return el;
  }

  /** The labelled variant. Built on demand — most venues never become one. */
  private pillFor(rec: MarkerRecord): HTMLElement {
    const v = rec.venue;
    const meta = kindMeta(v.kind, v.geocoded);
    const el = rec.pill ?? document.createElement('div');
    rec.pill = el;
    el.className = `pill${v.visited ? ' visited' : ''}${
      v.map_place_id === this.selected ? ' active' : ''
    }`;
    el.style.setProperty('--kc', meta.color);
    render(
      html`
        <span class="disc"><g-icon name=${v.visited ? 'check' : meta.icon}></g-icon></span>
        <span class="label">${v.name}</span>
        ${v.reels.length > 1 ? html`<span class="n">${v.reels.length}</span>` : nothing}
      `,
      el,
    );
    return el;
  }

  /**
   * Re-apply the active styling and stacking after the selection moves.
   *
   * Only the two affected markers actually change, but finding them costs the
   * same scan as restyling everything, and a pill that is already correct is
   * rebuilt into the identical DOM — cheaper than tracking the previous id.
   */
  private restyleSelection(): void {
    for (const rec of this.records) {
      const isActive = rec.venue.map_place_id === this.selected;
      rec.marker.zIndex = isActive ? 1_000_000 : rec.venue.reels.length;
      // The selected venue is always promoted to a pill, whatever the collision
      // pass would have said — see `pinned` in resolveLabels.
      if (isActive && rec.mode !== 'pill') {
        rec.mode = 'pill';
        rec.marker.content = this.pillFor(rec);
      } else if (rec.mode === 'pill') {
        this.pillFor(rec); // refresh the `active` class in place
      }
    }
  }

  /** Decide pill-vs-dot for what is on screen. Runs on every camera settle. */
  private runCollision(): void {
    if (!this.map || !this.records.length) return;
    const zoom = this.map.getZoom();
    const bounds = this.map.getBounds();
    if (zoom == null || !bounds) return;
    // When clustering is active at this zoom there are no individual markers to
    // label. An unclustered set is labelled at every zoom.
    if (this.clusterer && zoom <= CLUSTER_MAX_ZOOM) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const items: Placeable[] = this.records.map((rec) => ({
      lat: rec.venue.lat as number,
      lng: rec.venue.lng as number,
      width: estimateWidth(rec.venue.name, rec.venue.reels.length > 1),
      priority: rec.venue.reels.length,
      pinned: rec.venue.map_place_id === this.selected,
    }));

    const modes = resolveLabels(items, {
      zoom,
      north: ne.lat(),
      south: sw.lat(),
      east: ne.lng(),
      west: sw.lng(),
    });

    for (const [i, mode] of modes) {
      const rec = this.records[i];
      if (!rec || rec.mode === mode) continue;
      rec.mode = mode;
      rec.marker.content = mode === 'pill' ? this.pillFor(rec) : rec.dot;
    }
  }

  // ── the selected-venue card ────────────────────────────────

  private drawCard(): void {
    if (this.cardMarker) {
      this.cardMarker.map = null;
      this.cardMarker = undefined;
    }
    if (!this.map || !this.cards || this.selected == null) return;
    const venue = this.venues.find((v) => v.map_place_id === this.selected);
    if (!venue?.geocoded) return;

    const card = document.createElement('venue-card');
    card.venue = venue;
    // Without this a tap on the card reaches the map's click handler underneath
    // and immediately dismisses what was just opened.
    for (const type of ['click', 'pointerdown']) {
      card.addEventListener(type, (e) => e.stopPropagation());
    }
    card.addEventListener('close', () => this.emit('venue-deselect', null));

    this.cardMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: { lat: venue.lat as number, lng: venue.lng as number },
      content: card,
      zIndex: 2_000_000,
    });
  }

  // ── camera ─────────────────────────────────────────────────

  /** The home set, minus anything that has no pin to frame. */
  private get homePinned(): readonly Venue[] {
    return this.home.filter((v) => v.geocoded && v.lat != null && v.lng != null);
  }

  /** Frame these venues. Public: the workspace calls it when a search narrows. */
  fitTo(venues: readonly Venue[]): void {
    const pts = venues.filter((v) => v.geocoded && v.lat != null && v.lng != null);
    if (!this.map || !pts.length) return;
    const bounds = new google.maps.LatLngBounds();
    for (const v of pts) bounds.extend({ lat: v.lat as number, lng: v.lng as number });
    // fitBounds takes real per-edge padding, so the obscured bands go straight
    // in and no correction is needed afterwards — unlike panTo below.
    this.map.fitBounds(bounds, {
      top: 48 + Math.max(this.topInset, (this.scrim?.offsetHeight ?? 0) - 8),
      bottom: 48 + this.bottomInset,
      left: 48,
      right: 48,
    });
    // fitBounds takes no maxZoom, so cap it once the fit settles. One-shot: a
    // standing listener would fight every later zoom the user asks for.
    const once = google.maps.event.addListener(this.map, 'idle', () => {
      once.remove();
      if ((this.map?.getZoom() ?? 0) > FIT_MAX_ZOOM) this.map?.setZoom(FIT_MAX_ZOOM);
    });
  }

  /**
   * Put one venue in the middle of the *visible* map. Flies rather than jumps;
   * never zooms out.
   *
   * "Visible" is the div minus whatever is sitting over it — the status-bar wash
   * above, the sheet below. `panTo` centres on the whole div, so on mobile the
   * venue would land under the sheet.
   *
   * The offset is applied to the target centre in Web Mercator rather than
   * corrected afterwards with `panBy`, so the whole thing stays one continuous
   * flight instead of a move followed by a lurch.
   */
  panTo(venue: Venue): void {
    if (!venue.geocoded || venue.lat == null || venue.lng == null) return;
    if (!this.map) {
      this.pendingFocus = venue;
      return;
    }
    const zoom = Math.max(this.map.getZoom() ?? 0, SELECT_ZOOM);
    this.flyTo({ lat: this.shiftLat(venue.lat, zoom), lng: venue.lng }, zoom);
  }

  /**
   * Move a latitude south by half the difference between the obscured bands, so
   * that a point drawn there appears centred between them.
   *
   * Web Mercator, normalised to 0..1 increasing southward, where the world is
   * 256px at zoom 0:
   *
   *     y  = (1 - ln(tan φ + sec φ) / π) / 2
   *     y' = y + Δ / (256 · 2^zoom)
   *     φ' = atan(sinh(π(1 - 2y')))
   *
   * Δ is positive when the bottom is more obscured than the top, which is every
   * case here — so the centre moves south and the venue rides up above it.
   */
  private shiftLat(lat: number, zoom: number): number {
    // The scrim's own box, minus the 8px of fade it adds below the inset. Read
    // from layout rather than from the token, because env() is only resolved by
    // the browser and only where a cutout actually exists — 0 everywhere else.
    const top = Math.max(0, (this.scrim?.offsetHeight ?? 0) - 8);
    // The projection itself MOVED to packages/core (ADR 0032 decision 6's rule)
    // when the native camera contract needed the identical arithmetic. What
    // stays here is the part that is this client's own: which of its elements
    // obscure which edge.
    return shiftLatBy(lat, zoom, (this.bottomInset - top) / 2);
  }

  /**
   * Animate the camera to a centre and zoom.
   *
   * Google's own `panTo` only animates a move small enough to stay on screen and
   * hard-cuts anything further, and `setZoom` never animates at all — so the two
   * together produced a jump-cut from wherever you were straight into a street.
   * Nothing in the API does a proper flight, so this interpolates the camera
   * itself with `moveCamera` on each frame.
   *
   * The zoom dips in the middle for long moves — the "zoom out, travel, zoom in"
   * arc every map app uses — because sliding across continents at close zoom is
   * an unreadable blur. `dip` is how far out you would have to be to see both
   * ends at once, so a short hop gets no dip at all and the path stays flat.
   */
  private flyTo(center: google.maps.LatLngLiteral, zoom: number): void {
    const map = this.map;
    if (!map) return;
    cancelAnimationFrame(this.flight);

    const from = map.getCenter()?.toJSON();
    const z0 = map.getZoom();
    const land = () => map.moveCamera({ center, zoom });
    if (!from || z0 == null) return land();
    // Someone who has asked for less motion gets none: this is a large moving
    // surface, which is exactly what that preference is about.
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return land();

    const dLat = center.lat - from.lat;
    const dLng = center.lng - from.lng;
    const span = Math.max(Math.abs(dLat), Math.abs(dLng));
    // Zoom at which `span` degrees spans the viewport; -1 for margin.
    const fitZoom = span > 0.0001 ? Math.log2(360 / span) - 1 : Infinity;
    const dip = Math.min(5, Math.max(0, Math.min(z0, zoom) - fitZoom));
    const ms = Math.min(FLIGHT_MAX_MS, FLIGHT_MIN_MS + dip * 120);

    const started = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - started) / ms);
      // easeInOutCubic — accelerates away and settles rather than stopping dead
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      map.moveCamera({
        center: { lat: from.lat + dLat * e, lng: from.lng + dLng * e },
        // sin() is 0 at both ends, so the dip cannot disturb where we land
        zoom: z0 + (zoom - z0) * e - dip * Math.sin(Math.PI * e),
      });
      if (t < 1) this.flight = requestAnimationFrame(step);
    };
    this.flight = requestAnimationFrame(step);
  }

  private emitCamera(): void {
    const b = this.map?.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    this.dispatchEvent(
      new CustomEvent('camera', {
        detail: { north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private emit(type: string, detail: number | null): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  override render() {
    return html`
      <div id="map"></div>
      <div class="scrim"></div>
      ${this.failed
        ? html`<div class="overlay">
            <g-notice tone="danger">${this.failed}</g-notice>
          </div>`
        : nothing}
      ${!this.ready && !this.failed
        ? html`<div class="overlay"><g-notice>Loading the map…</g-notice></div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'map-canvas': MapCanvas;
  }
}
